<template>
  <div class="content">
    <div class="content__timeframe">
      <div class="content__timeframe__start-time">
        <div class="content__timeframe__start-time__label" data-test="timeframe_start_time_txt">
          {{ $t('amd:alerts_configuration_threshold_label_from') }}
        </div>
        <le-select class="content__timeframe__start-time__select"
                   theme="theme-light"
                   type="dropdown"
                   ref="startTimeValue"
                   v-model="startTimeValue"
                   :items="getTimeFrameOptions()"
                   :dropdownDefaultSelection="false"
                   :placeholder="`${$t('amd:alerts_configuration_timeframe_placeholder')}`"
                   :maxItemsToShow="6"
                   @input="onInputChange()"
                   data-test="timeframe_start_time_select"
                   />
      </div>
      <div class="content__timeframe__separator"/>
      <div class="content__timeframe__end-time">
        <div class="content__timeframe__end-time__label" data-test="timeframe_start_time_end_time_txt">
          {{ $t('amd:alerts_configuration_threshold_label_to') }}
        </div>
        <le-select class="content__timeframe__end-time__select"
                   theme="theme-light"
                   type="dropdown"
                   ref="endTimeValue"
                   v-model="endTimeValue"
                   :items="getTimeFrameOptions()"
                   :dropdownDefaultSelection="false"
                   :placeholder="`${$t('amd:alerts_configuration_timeframe_placeholder')}`"
                   :maxItemsToShow="6"
                   @input="onInputChange()"
                   data-test="timeframe_end_time_select"
                   />
      </div>
      <div class="content__timeframe__type">
        <div class="content__timeframe__type__label" data-test="timeframe_config_type_txt">
          {{ $t('amd:alerts_configuration_type_label') }}
        </div>
        <le-select class="content__timeframe__type__select"
                   theme="theme-light"
                   type="dropdown"
                   ref="conditionType"
                   v-model="selectedConditionType"
                   :items="conditionTypes"
                   :placeholder="`${$t('amd:alerts_configuration_type_combo_placeholder')}`"
                   @input="onConditionTypeChange()"
                   data-test="timeframe_config_type_select"
        />
      </div>
      <div v-if="showThresholdInput()"
           class="content__timeframe__threshold">
        <div class="content__timeframe__threshold__from">
          <div class="content__timeframe__threshold__from__label" data-test="timeframe_threshold_from_txt">
            {{ getThresholdFromLabel }}
          </div>
          <le-input-field class="content__timeframe__threshold__from__input"
                          ref="thresholdValueFrom"
                          v-model="thresholdValueFrom"
                          :maxChars="maxLength"
                          inputType="positiveIntegerOrZero"
                          inputPaddingLeft="20px"
                          :placeholder="`${$t('amd:alerts_configuration_duration_value_placeholder')}`"
                          @input="onInputChange"
                          data-test="timeframe_threshold_from_value"
          />
          <i class="content__timeframe__threshold__from__sign" data-test="timeframe_threshold_from_percent_txt">%</i>
        </div>
        <div v-if="isConditionTypeRange()"
             class="content__timeframe__threshold__separator"/>
        <div v-if="isConditionTypeRange()"
             class="content__timeframe__threshold__to">
          <div class="content__timeframe__threshold__to__label" data-test="timeframe_threshold_to_txt">
            {{ $t('amd:alerts_configuration_threshold_label_to') }}
          </div>
          <le-input-field class="content__timeframe__threshold__to__input"
                          ref="thresholdValueTo"
                          v-model="thresholdValueTo"
                          :maxChars="maxLength"
                          inputType="positiveIntegerOrZero"
                          inputPaddingLeft="20px"
                          :placeholder="`${$t('amd:alerts_configuration_duration_value_placeholder')}`"
                          @input="onInputChange"
                          data-test="timeframe_threshold_to_value"
          />
          <i class="content__timeframe__threshold__from__sign" data-test="timeframe_threshold_to_percent_txt">%</i>
        </div>
      </div>
      <le-button class="content__timeframe__reset"
                 variant="link"
                 :label="$t('amd:alerts_configuration_threshold_clear')"
                 :disabled="isRowEmpty"
                 @click="onResetTimeframeClick()"
                 data-test="timeframe_reset_btn"
                 >
      </le-button>
      <general-icon v-if="index !== 0"
                    icon="close-icon"
                    class="content__timeframe__remove"
                    tabindex="0"
                    role="button"
                    :aria-label="$t('amd:alerts_configuration_threshold_remove')"
                    @keydown.space.enter.prevent="onRemoveTimeframeClick()"
                    @click.native="onRemoveTimeframeClick()"
                    data-test="timeframe_close_btn"
      />
    </div>
    <div class="content__error" data-test="timeframe_error_txt">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
/* eslint-disable max-len, no-unused-expressions */
import moment from 'moment';
import { leSelect, leInputField, leButton } from 'lp-vue-components';
import { map, get, isEmpty, each, isUndefined } from 'lodash';
import { ALERT_CONDITION_TYPES, DEFAULT_FIELD_MAX_LENGTH } from '@config/Consts';
import GeneralIcon from '@components/common/icons/general-icon';

export default {
  name: 'timeframe',
  components: {
    leSelect,
    leInputField,
    leButton,
    GeneralIcon,
  },
  props: {
    index: {
      type: Number,
      required: true,
    },
    validation: {
      type: Object,
      default: undefined,
      required: true,
    },
    timeframeValue: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      conditionTypes: map(ALERT_CONDITION_TYPES, item => ({ id: item, value: item })),
      changeEventEmitted: false,
      id: null,
      startTimeValue: null,
      endTimeValue: null,
      selectedConditionType: null,
      thresholdValueFrom: [],
      thresholdValueTo: [],
      showErrorMessage: false,
      errorMessage: '',
    };
  },
  computed: {
    getThresholdFromLabel() {
      return this.isConditionTypeRange() ? this.$t('amd:alerts_configuration_threshold_label_from') : this.$t('amd:alerts_configuration_threshold_label');
    },
    maxLength() {
      const maxValidNumber = this.validation && Math.max(Math.abs(this.validation.from), Math.abs(this.validation.to));
      return isUndefined(maxValidNumber) ? DEFAULT_FIELD_MAX_LENGTH : maxValidNumber.toString().length;
    },
    isRowEmpty() {
      return isEmpty(this.startTimeValue) &&
        isEmpty(this.endTimeValue) &&
        isEmpty(this.selectedConditionType) &&
        isEmpty(this.thresholdValueFrom) &&
        isEmpty(this.thresholdValueTo);
    },
  },
  watch: {
    timeframeValue: {
      handler() {
        if (this.timeframeValue) {
          this.id = get(this.timeframeValue, 'id');
          this.startTimeValue = get(this.timeframeValue, 'startTimeValue', '');
          this.endTimeValue = get(this.timeframeValue, 'endTimeValue', '');
          this.selectedConditionType = get(this.timeframeValue, 'conditionType');
          this.thresholdValueFrom = get(this.timeframeValue, 'thresholdValueFrom', '');
          this.thresholdValueTo = get(this.timeframeValue, 'thresholdValueTo', '');
        }
      },
      immediate: true,
    },
  },
  methods: {
    isConditionTypeRange() {
      return get(this.selectedConditionType, 'id') === ALERT_CONDITION_TYPES.BETWEEN;
    },
    showThresholdInput() {
      return !isEmpty(this.selectedConditionType);
    },
    getTimeFrameOptions() {
      const interval = 30;
      const period = 'm';
      const periodsInADay = moment.duration(1, 'day').as(period);

      const timeLabels = [];
      const startTimeMoment = moment('00:00', 'hh:mm');
      for (let i = 0; i <= periodsInADay; i += interval) {
        startTimeMoment.add(i === 0 ? 0 : interval, period);
        timeLabels.push({ id: i, value: startTimeMoment.format('LT'), valueAbsMinutes: i });
      }

      return timeLabels;
    },
    onConditionTypeChange() {
      this.thresholdValueTo = '';
      this.$emit('onConditionTypeChange', this.index);
    },
    onResetTimeframeClick() {
      this.resetInputs();
      this.$emit('onResetTimeframeClick', this.index);
    },
    onRemoveTimeframeClick() {
      this.resetInputs();
      this.$emit('onRemoveTimeframeClick', this.index);
    },
    resetInputs() {
      this.clearError();
      this.startTimeValue = {};
      this.endTimeValue = {};
      this.selectedConditionType = null;
      this.thresholdValueFrom = '';
      this.thresholdValueTo = '';
    },
    onInputChange() {
      this.$emit('onInputChange', this.index);
    },
    validate() {
      const rangeFrom = this.validation.from;
      const rangeTo = this.validation.to;
      let isValid = true;
      if (isEmpty(this.startTimeValue) && isEmpty(this.endTimeValue) && isEmpty(this.selectedConditionType)) {
        return true;
      }
      const emptyInputsArray = this.getEmptyInputsArray();
      isValid = emptyInputsArray.length === 0;
      if (!isValid) {
        const missingValueMessage = this.$t('amd:alerts_configuration_threshold_required_validation');
        this.setInputErrors(emptyInputsArray, true, missingValueMessage);
        return false;
      }
      isValid = this.isTimeframeValid();
      if (!isValid) {
        this.setInputErrors([this.$refs.startTimeValue, this.$refs.endTimeValue], true, this.$t('amd:alerts_configuration_timeframe_time_validation'));
        return false;
      }
      isValid = this.isThresholdFromValid();
      if (!isValid) {
        this.setInputErrors([this.$refs.thresholdValueFrom], true, this.$t('amd:alerts_configuration_threshold_range_validation', { from: rangeFrom, to: rangeTo }));
        return false;
      }

      if (this.isConditionTypeRange()) {
        isValid = this.isThresholdToValid();
        if (!isValid) {
          this.setInputErrors([this.$refs.thresholdValueTo], true, this.$t('amd:alerts_configuration_threshold_range_validation', { from: rangeFrom, to: rangeTo }));
          return false;
        }

        isValid = this.isThresholdRangeValid();
        if (!isValid) {
          this.setInputErrors([this.$refs.thresholdValueFrom, this.$refs.thresholdValueTo], true, this.$t('amd:alerts_configuration_threshold_range_validation_tooltip'));
          return false;
        }
      }
      return isValid;
    },
    getEmptyInputsArray() {
      const inputs = [];
      if (isEmpty(this.startTimeValue)) {
        inputs.push(this.$refs.startTimeValue);
      }
      if (isEmpty(this.endTimeValue)) {
        inputs.push(this.$refs.endTimeValue);
      }
      if (isEmpty(this.selectedConditionType)) {
        inputs.push(this.$refs.conditionType);
      } else if (this.isConditionTypeRange()) {
        if (isEmpty(this.thresholdValueFrom)) {
          inputs.push(this.$refs.thresholdValueFrom);
        }
        if (isEmpty(this.thresholdValueTo)) {
          inputs.push(this.$refs.thresholdValueTo);
        }
      } else if (isEmpty(this.thresholdValueFrom)) {
        inputs.push(this.$refs.thresholdValueFrom);
      }
      return inputs;
    },
    isTimeframeValid() {
      return this.endTimeValue.valueAbsMinutes > this.startTimeValue.valueAbsMinutes;
    },
    isThresholdFromValid() {
      return this.validateThreshold(this.thresholdValueFrom);
    },
    isThresholdToValid() {
      return this.validateThreshold(this.thresholdValueTo);
    },
    validateThreshold(value) {
      const rangeFrom = this.validation.from;
      const rangeTo = this.validation.to;
      const isValidType = !isNaN(Number(value));
      const isInRange = isValidType && Number(value) >= rangeFrom && Number(value) <= rangeTo;
      return isValidType && isInRange;
    },
    isThresholdRangeValid() {
      const isValidType = !isNaN(Number(this.thresholdValueFrom)) || !isNaN(Number(this.thresholdValueTo));
      const isInRange = isValidType && Number(this.thresholdValueFrom) < Number(this.thresholdValueTo);
      return isValidType && isInRange;
    },
    setInputErrors(controls, showError, errorMessage) {
      each(controls, ref => (ref && (showError ? ref.$el.classList.add('error') : ref.$el.classList.remove('error'))));
      this.showErrorMessage = showError;
      this.errorMessage = errorMessage;
    },
    clearError() {
      const controls = [
        this.$refs.startTimeValue,
        this.$refs.endTimeValue,
        this.$refs.conditionType,
        this.$refs.thresholdValueFrom,
        this.$refs.thresholdValueTo,
      ];
      this.setInputErrors(controls, false, '');
    },
    getTimeframeData() {
      return this.isControlsEmpty() ? null : {
        id: this.id,
        startTimeValue: this.startTimeValue,
        endTimeValue: this.endTimeValue,
        conditionType: this.selectedConditionType,
        thresholdValueFrom: this.thresholdValueFrom,
        thresholdValueTo: this.thresholdValueTo,
      };
    },
    isControlsEmpty() {
      return isEmpty(this.startTimeValue) && isEmpty(this.endTimeValue) &&
        isEmpty(this.selectedConditionType) && isEmpty(this.thresholdValueFrom) &&
        isEmpty(this.thresholdValueTo);
    },
    setTimeframeError() {
      const errorMsg = this.$t('amd:alerts_configuration_timeframe_time_overlap');
      this.setInputErrors([this.$refs.startTimeValue, this.$refs.endTimeValue], true, errorMsg);
    },
    setTimeframeEndError() {
      const errorMsg = this.$t('amd:alerts_configuration_timeframe_time_overlap');
      this.setInputErrors([this.$refs.endTimeValue], true, errorMsg);
    },
    setTimeframeStartError() {
      const errorMsg = this.$t('amd:alerts_configuration_timeframe_time_overlap');
      this.setInputErrors([this.$refs.startTimeValue], true, errorMsg);
    },
  },
};
</script>

<style lang="scss" scoped>
.content {
  &__timeframe {
    display: flex;
    align-items: center;
    margin-bottom: 10px;

    &__start-time, &__end-time {
      width: 110px;

      &__label {
        margin-bottom: 5px;
      }
    }

    &__separator {
      width: 5px;
      height: 1px;
      margin: 16px 10px 0;
      @include themify {
        background-color: getColor('mainFilterSeparator', 'amd');
      }
    }

    &__type {
      width: 110px;
      margin-left: 10px;

      &__label {
        margin-bottom: 5px;
      }
    }

    &__threshold {
      display: flex;
      align-items: center;

      &__from {
        margin-left: 10px;
      }

      &__from, &__to {
        width: 70px;
        position: relative;

        &__label {
          margin-bottom: 5px;
        }

        &__sign {
          position: absolute;
          transform: translate(0, -170%);
          pointer-events: none;
          width: 25px;
          text-align: center;
        }
      }

      &__separator {
        width: 5px;
        height: 1px;
        margin: 16px 10px 0;
        @include themify {
          background-color: getColor('mainFilterSeparator', 'amd');
        }
      }
    }

    &__reset {
      margin: 18px 0 0 10px;
    }

    &__remove {
      margin: 20px 0 0 10px;
    }

    .error {
      @include themify {
        border: 1px solid getColor('alertValidationError', 'amd');
      }
    }
  }
  &__error {
    margin-bottom: 10px;
    min-height: 13px;
    @include themify {
      color: getColor('alertValidationError', 'amd');
    }
  }
}
</style>
